import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Badge, Spinner, EmptyState } from "@/components/ui";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import toast from "react-hot-toast";
import clsx from "clsx";

const TYPE_COLORS = { irl: "cyan", digital: "purple", quiz: "gold" };
const TYPE_LABELS = { irl: "🌍 IRL", digital: "💻 Digital", quiz: "⚡ Quiz" };
const TASK_EMOJIS = ["📌","🎯","🏃","🌱","📸","🧪","🎨","🏋️","📚","🌍","🤝","🔧","🎮","💡","🎤","🧹"];

export default function TaskManager() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("tasks"); // tasks | review | dreams
  const [denyModal, setDenyModal] = useState(null);
  const [denyNote, setDenyNote] = useState("");
  const [modal, setModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [form, setForm] = useState({
    title: "", description: "", emoji: "📌", type: "digital",
    xpReward: 100, coinsReward: 50, difficulty: 1, requiresProof: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => api.get("/tasks").then((r) => r.data),
  });

  const { data: pendingData, isLoading: loadingPending } = useQuery({
    queryKey: ["admin", "pending-tasks"],
    queryFn: () => api.get("/admin/tasks/pending").then((r) => r.data),
    refetchInterval: 15_000,
  });

  // Belohnungszentrum "Träume" — big, real-life reward requests awaiting approval
  const { data: dreamsData, isLoading: loadingDreams } = useQuery({
    queryKey: ["admin", "pending-dreams"],
    queryFn: () => api.get("/admin/dreams/pending").then((r) => r.data),
    refetchInterval: 15_000,
  });

  const create = useMutation({
    mutationFn: () => api.post("/tasks", form),
    onSuccess: () => {
      toast.success("Aufgabe erstellt! 📌");
      qc.invalidateQueries({ queryKey: ["tasks"] });
      setModal(false);
      setForm({ title: "", description: "", emoji: "📌", type: "digital", xpReward: 100, coinsReward: 50, difficulty: 1, requiresProof: false });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const approve = useMutation({
    mutationFn: (id) => api.post(`/admin/tasks/${id}/approve`),
    onSuccess: () => {
      toast.success("✅ Aufgabe genehmigt!");
      qc.invalidateQueries({ queryKey: ["admin", "pending-tasks"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const reject = useMutation({
    mutationFn: (id) => api.post(`/admin/tasks/${id}/reject`, { note: rejectNote }),
    onSuccess: () => {
      toast.success("❌ Aufgabe abgelehnt");
      qc.invalidateQueries({ queryKey: ["admin", "pending-tasks"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      setRejectModal(null);
      setRejectNote("");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/tasks/${id}`),
    onSuccess: () => {
      toast.success("Aufgabe gelöscht");
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const approveDream = useMutation({
    mutationFn: (id) => api.post(`/admin/dreams/${id}/approve`),
    onSuccess: () => {
      toast.success("🎉 Traum genehmigt!");
      qc.invalidateQueries({ queryKey: ["admin", "pending-dreams"] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const denyDream = useMutation({
    mutationFn: (id) => api.post(`/admin/dreams/${id}/deny`, { note: denyNote }),
    onSuccess: () => {
      toast.success("Traum-Anfrage abgelehnt");
      qc.invalidateQueries({ queryKey: ["admin", "pending-dreams"] });
      setDenyModal(null);
      setDenyNote("");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const tasks   = data?.tasks || [];
  const pending = pendingData?.tasks || [];
  const dreams  = dreamsData?.requests || [];

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black">📌 Aufgaben</h1>
          <p className="text-xs text-gray-400 mt-0.5">{tasks.length} Aufgaben · {pending.length} zu prüfen</p>
        </div>
        <Button size="sm" onClick={() => setModal(true)}>+ Neu</Button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2">
        {[["tasks", "📋 Aufgaben"], ["review", `🔍 Prüfen (${pending.length})`], ["dreams", `🌌 Träume (${dreams.length})`]].map(([t, l]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              "flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all",
              t === tab ? "bg-violet-600 border-violet-500 text-white" : "bg-nexus-s2 border-nexus-border text-gray-400"
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "tasks" && (
        isLoading ? <div className="flex justify-center pt-10"><Spinner size="lg" /></div> :
        tasks.length === 0 ? (
          <EmptyState icon="📌" title="Keine Aufgaben" desc="Erstelle die erste Aufgabe!" action={<Button onClick={() => setModal(true)}>+ Aufgabe erstellen</Button>} />
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => (
              <Card key={t.id} className="p-3.5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">{t.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{t.title}</p>
                    <div className="flex gap-1.5 mt-1 flex-wrap">
                      <Badge color={TYPE_COLORS[t.type]}>{TYPE_LABELS[t.type]}</Badge>
                      <span className="text-[10px] text-violet-400">+{t.xpReward} XP</span>
                      <span className="text-[10px] text-yellow-400">+{t.coinsReward} 🪙</span>
                      {t.requiresProof && <Badge color="cyan">📷</Badge>}
                      <span className="text-[10px] text-gray-500">{t._count?.completions || 0}× erledigt</span>
                    </div>
                  </div>
                  <button onClick={() => del.mutate(t.id)} className="text-red-400 hover:text-red-300 p-1">🗑️</button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "review" && (
        loadingPending ? <div className="flex justify-center pt-10"><Spinner size="lg" /></div> :
        pending.length === 0 ? (
          <EmptyState icon="✅" title="Alles geprüft!" desc="Keine offenen Abgaben." />
        ) : (
          <div className="space-y-3">
            {pending.map((comp) => (
              <Card key={comp.id} className="p-4 border-yellow-500/20">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">{comp.task?.emoji}</span>
                  <div className="flex-1">
                    <p className="font-black text-sm">{comp.task?.title}</p>
                    <p className="text-xs text-gray-400">von {comp.student?.user?.name}</p>
                    {comp.note && (
                      <p className="text-xs text-gray-300 mt-1 bg-nexus-s2 rounded-lg p-2">
                        💬 "{comp.note}"
                      </p>
                    )}
                    {comp.proofUrl && (
                      <a href={comp.proofUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 underline mt-1 block">
                        📎 Beweis ansehen
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    full
                    variant="success"
                    size="sm"
                    loading={approve.isPending}
                    onClick={() => approve.mutate(comp.id)}
                  >
                    ✅ Genehmigen
                  </Button>
                  <Button
                    full
                    variant="danger"
                    size="sm"
                    onClick={() => setRejectModal(comp.id)}
                  >
                    ❌ Ablehnen
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "dreams" && (
        loadingDreams ? <div className="flex justify-center pt-10"><Spinner size="lg" /></div> :
        dreams.length === 0 ? (
          <EmptyState icon="🌌" title="Keine Traum-Anfragen" desc="Schüler:innen fragen große Belohnungen aus dem Belohnungszentrum an." />
        ) : (
          <div className="space-y-3">
            {dreams.map((req) => (
              <Card key={req.id} className="p-4 border-pink-500/20">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">🌌</span>
                  <div className="flex-1">
                    <p className="font-black text-sm">{req.dreamName}</p>
                    <p className="text-xs text-gray-400">von {req.studentProfile?.user?.name} · {req.cost.toLocaleString("de-DE")} 🪙</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button full variant="success" size="sm" loading={approveDream.isPending} onClick={() => approveDream.mutate(req.id)}>
                    ✅ Genehmigen
                  </Button>
                  <Button full variant="danger" size="sm" onClick={() => setDenyModal(req.id)}>
                    ❌ Ablehnen
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Create task modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="📌 Neue Aufgabe">
        <div className="space-y-4">
          <Input label="Titel" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} placeholder="Aufgabentitel..." />

          <div>
            <p className="text-xs font-bold text-gray-400 mb-2">Emoji</p>
            <div className="flex flex-wrap gap-2">
              {TASK_EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => setForm((f) => ({ ...f, emoji: e }))}
                  className={clsx(
                    "w-9 h-9 rounded-lg text-lg border-2 transition-all",
                    form.emoji === e ? "border-violet-500 bg-violet-500/20" : "border-nexus-border bg-nexus-s2"
                  )}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Beschreibung / Anweisungen..."
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none h-24 focus:outline-none focus:border-violet-500"
          />

          <div>
            <p className="text-xs font-bold text-gray-400 mb-2">Typ</p>
            <div className="flex gap-2">
              {["irl", "digital"].map((t) => (
                <button
                  key={t}
                  onClick={() => setForm((f) => ({ ...f, type: t }))}
                  className={clsx(
                    "flex-1 py-2 rounded-xl text-xs font-bold border transition-all",
                    form.type === t ? "bg-violet-600 border-violet-500 text-white" : "bg-nexus-s2 border-nexus-border text-gray-400"
                  )}
                >
                  {TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input label="XP" type="number" value={String(form.xpReward)} onChange={(v) => setForm((f) => ({ ...f, xpReward: Number(v) }))} />
            <Input label="Coins" type="number" value={String(form.coinsReward)} onChange={(v) => setForm((f) => ({ ...f, coinsReward: Number(v) }))} />
          </div>

          <div className="flex items-center justify-between p-3 bg-nexus-s2 rounded-xl border border-nexus-border">
            <div>
              <p className="text-sm font-bold">📷 Beweis erforderlich</p>
              <p className="text-xs text-gray-500">Schüler müssen Foto/Video einreichen</p>
            </div>
            <button
              onClick={() => setForm((f) => ({ ...f, requiresProof: !f.requiresProof }))}
              className={clsx(
                "relative w-11 h-6 rounded-full transition-colors",
                form.requiresProof ? "bg-violet-600" : "bg-nexus-s3 border border-nexus-border"
              )}
            >
              <span className={clsx("absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all", form.requiresProof ? "left-6" : "left-1")} />
            </button>
          </div>

          <Button full loading={create.isPending} onClick={() => create.mutate()}>
            📌 Aufgabe erstellen
          </Button>
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="❌ Aufgabe ablehnen">
        <div className="space-y-4">
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Grund für die Ablehnung (wird dem Schüler angezeigt)..."
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none h-24 focus:outline-none focus:border-violet-500"
          />
          <Button full variant="danger" loading={reject.isPending} onClick={() => reject.mutate(rejectModal)}>
            ❌ Ablehnen
          </Button>
        </div>
      </Modal>

      {/* Deny dream modal */}
      <Modal open={!!denyModal} onClose={() => setDenyModal(null)} title="❌ Traum ablehnen">
        <div className="space-y-4">
          <textarea
            value={denyNote}
            onChange={(e) => setDenyNote(e.target.value)}
            placeholder="Grund für die Ablehnung (wird dem Schüler angezeigt)..."
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none h-24 focus:outline-none focus:border-violet-500"
          />
          <Button full variant="danger" loading={denyDream.isPending} onClick={() => denyDream.mutate(denyModal)}>
            ❌ Ablehnen
          </Button>
        </div>
      </Modal>
    </div>
  );
}
